import React from "react";
import { Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";

const IMG = {
  tanker: "https://images.unsplash.com/photo-1598408745613-178751e2ccde",
  port: "https://images.pexels.com/photos/16520011/pexels-photo-16520011.jpeg",
};

const TickerRow = () => {
  const items = [
    "ICPO INTAKE — VETTED CORPORATE BUYERS",
    "NCNDA · IMFPA · SPA — LEGAL HIERARCHY ENFORCED",
    "BRENT · WTI · GASOIL · JET A-1",
    "ICUMSA 45 · GREEN COFFEE — ARABICA / ROBUSTA",
    "BCL VERIFIED — IRREVOCABLE PROOF OF FUNDS",
    "PURE BROKERAGE — WE NEVER TOUCH THE PRODUCT",
  ];
  const row = items.join("   ·   ");
  return (
    <div className="border-y border-line bg-parchment overflow-hidden">
      <div className="flex whitespace-nowrap animate-ticker font-mono text-[11px] tracking-[0.25em] text-ink2 py-3">
        <span className="px-8">{row}</span>
        <span className="px-8">{row}</span>
        <span className="px-8">{row}</span>
      </div>
    </div>
  );
};

export default function Home() {
  return (
    <div className="bg-parchment min-h-screen" data-testid="home-page">
      <PublicNav />

      {/* HERO */}
      <section className="relative h-[88vh] min-h-[640px] w-full overflow-hidden grain">
        <img src={IMG.tanker} alt="Crude tanker at dusk"
             className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/65 to-ink/90" />
        <div className="relative max-w-7xl mx-auto h-full px-6 md:px-10 flex flex-col justify-end pb-20">
          <div className="overline !text-brass mb-6 animate-fade-up">
            DELAWARE LLC — PURE BROKERAGE · NO INVENTORY
          </div>
          <h1 className="h-serif text-parchment text-5xl md:text-7xl lg:text-8xl leading-[0.95] max-w-5xl animate-fade-up" style={{ animationDelay: "120ms" }}>
            We don&apos;t move the cargo.<br />
            <span className="italic text-brass">We move the deal —</span><br />
            with legal protections that hold.
          </h1>
          <p className="mt-8 max-w-xl text-parchment/80 text-base md:text-lg leading-relaxed animate-fade-up" style={{ animationDelay: "240ms" }}>
            Campbell&apos;s Commodities Brokerage is a deal-flow intermediary between vetted
            corporate buyers and refineries, sugar mills and coffee origins. Every
            transaction runs through our enforced legal sequence —
            <span className="text-brass"> NCNDA → IMFPA → SPA</span> — so the
            introduction is sealed before the parties ever shake hands.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "360ms" }}>
            <Link to="/login" className="btn-primary" data-testid="hero-request-access">
              Submit an ICPO →
            </Link>
            <Link to="/process" className="btn-ghost !text-parchment !border-parchment hover:!bg-parchment hover:!text-ink" data-testid="hero-view-process">
              How the Deal Closes
            </Link>
          </div>
        </div>
      </section>

      <TickerRow />

      {/* DEAL-FLOW SUMMARY */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-28">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <div className="overline">Deal Flow Architecture</div>
            <h2 className="h-serif text-4xl md:text-5xl text-ink mt-4 leading-tight">
              Paperwork is the product. Sequence is the protection.
            </h2>
            <p className="text-sub mt-6 leading-relaxed">
              In a pure brokerage, the contract chain — not the cargo — is what gets you
              paid. Our platform automates that chain so a buyer and seller can never
              quietly close around us.
            </p>
            <Link to="/process" className="link mt-6 inline-block text-ink" data-testid="home-view-process">
              See the six-step pipeline →
            </Link>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {[
              { n: "01", t: "Compliance Gate",
                d: "Every buyer clears KYC, OFAC SDN and global sanctions screening through OpenSanctions before any commercial conversation begins. Background vetting is the first step, not an afterthought." },
              { n: "02", t: "Sanitized Intake",
                d: "Vetted buyers submit an ICPO + Bank Comfort Letter through their dashboard. The platform strips their identity before any supplier sees the request." },
              { n: "03", t: "Sequenced Lock-In",
                d: "NCNDA signed first — circumvention liability for 3–5 years. IMFPA lodged directly with the parties' banks. Only then is the SPA released." },
              { n: "04", t: "Bank-Routed Payout",
                d: "Buyer's LC funds clear into the seller's bank only after the IMFPA cuts our commission off the top. The introduction is sealed in the wire instructions." },
            ].map(({ n, t, d }) => (
              <div key={n} className="border-t border-ink/15 pt-6" data-testid={`value-prop-${n}`}>
                <div className="font-mono text-xs text-oxblood">{n}</div>
                <h3 className="h-serif text-2xl mt-2 text-ink">{t}</h3>
                <p className="text-sub text-sm mt-3 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMODITIES PREVIEW */}
      <section className="bg-parchment2 border-y border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-28 grid md:grid-cols-12 gap-14 items-center">
          <div className="md:col-span-6 relative">
            <img src={IMG.port} alt="Container port" className="w-full aspect-[4/5] object-cover" />
            <div className="absolute -bottom-6 -right-6 bg-oxblood text-parchment p-6 hidden md:block">
              <div className="overline !text-brass">Asset classes</div>
              <div className="h-serif text-3xl mt-1">Two markets. One desk.</div>
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="overline">Commodities &amp; Sectors</div>
            <h2 className="h-serif text-4xl md:text-5xl text-ink mt-3 leading-tight">
              Brokered categories — supplier rosters held in confidence.
            </h2>
            <div className="mt-10 divide-y divide-line border-y border-line">
              {[
                { k: "Energy", v: "Crude oil and refined petroleum products. Brent / WTI blends, gasoil, jet A-1, EN 590." },
                { k: "Soft Commodities", v: "Bulk sugar (ICUMSA 45) and green coffee — Arabica & Robusta origin sourcing." },
              ].map((row) => (
                <div key={row.k} className="py-6 grid grid-cols-12 gap-4 items-baseline">
                  <div className="col-span-4 h-serif text-2xl text-oxblood">{row.k}</div>
                  <div className="col-span-8 text-sub leading-relaxed">{row.v}</div>
                </div>
              ))}
            </div>
            <Link to="/commodities" className="link mt-8 inline-block text-ink" data-testid="home-view-all-commodities">
              View full asset class overview
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST CTA */}
      <section className="relative bg-ink text-parchment grain">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-32 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <div className="overline !text-brass">Vetted access only</div>
            <h2 className="h-serif text-5xl md:text-6xl mt-4 leading-[1.05]">
              No price list. No supplier roster. Just the deal — once the paperwork holds.
            </h2>
            <p className="text-parchment/70 mt-8 max-w-2xl leading-relaxed">
              Account approval requires a corporate identity, an ICPO template, and a
              Bank Comfort Letter from a top-tier institution. We trade trust for
              transparency, and we hold the line on the legal sequence.
            </p>
          </div>
          <div className="md:col-span-5 flex md:justify-end items-end">
            <Link to="/login" className="btn-brass" data-testid="cta-create-account">
              Create a Vetted Account →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
